import { motion } from "framer-motion";
import { Target, Compass } from "lucide-react";

const VISION = [
  "To build a portfolio of enduring Indian food & beverage brands.",
  "To redefine everyday consumer moments through quality and authenticity.",
  "To create brands that earn trust, loyalty, and lasting relevance.",
];

const MISSION = [
  "Partner with founders to transform promising ideas into scalable brands.",
  "Combine operational excellence with memorable consumer experiences.",
  "Build brands that are commercially strong, culturally relevant, and built to last.",
  "Uphold the highest standards of quality, consistency and authenticity.",
];

const PARAGRAPHS = [
  "Catalyst incubates and partners with food and beverage brands that we believe in — modern Indian businesses built on strong fundamentals, exceptional products, and compelling stories.",
  "We collaborate with founders to transform promising ideas into scalable brands, combining operational excellence with memorable consumer experiences. From concept creation and brand positioning to growth and expansion, we work closely with our partners to build businesses that are commercially strong, culturally relevant, and built for the long term.",
  "At Catalyst, we believe great food and beverage brands are about more than what is served — they are about the moments they create. A morning coffee before a busy day, a meal shared with family, or a gathering among friends; these everyday experiences shape how people connect, celebrate, and create memories. Our focus is on building brands that become a meaningful part of those moments.",
  "We are passionate about creating concepts that customers genuinely love — brands that stand out for their quality, consistency, and authenticity. By combining thoughtful brand building with disciplined execution, we help create businesses that not only grow but earn trust, loyalty, and lasting relevance.",
  "Because the brands that endure aren't simply remembered for their products — they're remembered for the experiences they create and the relationships they build with their customers.",
];

export default function FmcgAbout() {
  return (
    <section
      id="about-fmcg"
      data-testid="fmcg-about-section"
      className="bg-[#FAFAF7] py-24 md:py-32"
    >
      <div className="cgp-container">
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.7 }}
          className="max-w-3xl"
        >
          <span className="cgp-overline">About the Brand House</span>
          <h2 className="font-serif font-light text-[#0A1F44] mt-6 text-4xl sm:text-5xl lg:text-6xl leading-[1.05] tracking-tight">
            Building brands
            <br />
            <span className="italic text-[#0A1F44]/70">that become part of moments.</span>
          </h2>
          <p className="mt-7 font-serif italic text-[#0A1F44]/80 text-xl md:text-2xl leading-snug max-w-2xl">
            From concept to shelf — brands built on quality, story, and trust.
          </p>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-60px" }}
          transition={{ duration: 0.7 }}
          className="mt-16 grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16"
        >
          <div className="lg:col-span-7 space-y-5">
            {PARAGRAPHS.map((p, i) => (
              <p
                key={i}
                className="text-[#0B1A36]/80 text-base md:text-[1.05rem] leading-relaxed"
              >
                {p}
              </p>
            ))}
          </div>

          <div className="lg:col-span-5 space-y-6">
            <div
              data-testid="fmcg-vision-card"
              className="bg-white border border-[#0A1F44]/10 p-8"
            >
              <div className="flex items-center gap-3 mb-5">
                <span className="flex items-center justify-center w-10 h-10 rounded-full bg-[#C8A24A]/12 border border-[#C8A24A]/30 text-[#C8A24A]">
                  <Target size={16} strokeWidth={1.8} />
                </span>
                <h3 className="font-serif text-xl text-[#0A1F44]">Vision</h3>
              </div>
              <ul className="space-y-3">
                {VISION.map((v) => (
                  <li
                    key={v}
                    className="flex items-start gap-3 text-[#0B1A36]/75 text-sm leading-relaxed"
                  >
                    <span className="w-1.5 h-1.5 rounded-full bg-[#C8A24A] mt-2 shrink-0" />
                    <span>{v}</span>
                  </li>
                ))}
              </ul>
            </div>

            <div
              data-testid="fmcg-mission-card"
              className="bg-[#0A1F44] text-white p-8"
            >
              <div className="flex items-center gap-3 mb-5">
                <span className="flex items-center justify-center w-10 h-10 rounded-full bg-[#C8A24A]/15 border border-[#C8A24A]/40 text-[#C8A24A]">
                  <Compass size={16} strokeWidth={1.8} />
                </span>
                <h3 className="font-serif text-xl text-white">Mission</h3>
              </div>
              <ul className="space-y-3">
                {MISSION.map((m) => (
                  <li
                    key={m}
                    className="flex items-start gap-3 text-white/75 text-sm leading-relaxed"
                  >
                    <span className="w-1.5 h-1.5 rounded-full bg-[#C8A24A] mt-2 shrink-0" />
                    <span>{m}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
}
